import { Button } from "@fluentui/react-components";
import { ReactComponent as ChatBotIcon } from "assets/chatBotIcon.svg";
import { SearchBoxField } from "components/controls/SearchBoxField";
import NeedsAttentionButton from "components/NeedsAttention/NeedsAttentionButton";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { useIsMobile } from "hooks/useIsMobile";
import { useSetAtom } from "jotai";
import { devices } from "library/constants";
import {
  IBenefitCategory,
  IBenefitImplementation,
  IBenefitPackage,
  IEmployeeView,
} from "library/types";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { chatPanelOpenAtom } from "store/UIHrPage";
import styled from "styled-components";
import { ResultsScore } from "../ResultsScore/ResultsScore";
import { AdminPageSection } from "./AdminPageSection/AdminPageSection";
import CompanyPageSection from "./CompanyPageSection/CompanyPageSection";
import { CountryPageSection } from "./CountryPageSection/CountryPageSection";
import { HubPageSection } from "./HubPageSection/HubPageSection";
import { ChatIcon } from "components/ChatPanel/ChatIcon";
// import { isListViewState } from "store/UIAdminPage";

type Props = {
  items?:
    | IBenefitCategory[]
    | IBenefitImplementation[]
    | IBenefitPackage[]
    | IEmployeeView[];

  setSearchFilterValue: (value: string) => void;
  disabled?: boolean;
  searchFilterValue: string;
  showSearchFilter?: boolean;
  isHrPage?: boolean;
  isCountryPage?: boolean;
  isCompanyPage?: boolean;
  isAdminPage?: boolean;
  chatBotDisabled?: boolean;
  showNeedsAttention?: boolean;
  isNeedsAttentionPage?: boolean;
  needsAttentionCount?: number;
};

export function Subheader({
  items,
  setSearchFilterValue,
  disabled,
  searchFilterValue,
  showSearchFilter = true,
  isAdminPage,
  isCountryPage,
  isHrPage,
  isCompanyPage,
  chatBotDisabled,
  showNeedsAttention = false,
  needsAttentionCount = 0,
  isNeedsAttentionPage,
}: Props) {
  const { palette, semanticColors } = useAppTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const setChatPanelOpen = useSetAtom(chatPanelOpenAtom);

  const handleSearch = useCallback(
    (value) => {
      setSearchFilterValue(value.toLowerCase());
    },
    [setSearchFilterValue]
  );

  const searchResultsCount = items
    ? items?.filter((item) => {
        if (item?.["name"]) {
          return item["name"].toLowerCase().includes(searchFilterValue);
        }
        return true;
      }).length
    : 0;

  return (
    <Container $showSearchFilter={showSearchFilter}>
      <Wrap>
        {/* <Navigation /> */}
        {isAdminPage ? <AdminPageSection /> : null}
        {isHrPage ? <HubPageSection /> : null}
        {isCountryPage ? (
          <CountryPageSection setSearchFilterValue={setSearchFilterValue} />
        ) : null}

        {isCompanyPage ? <CompanyPageSection /> : null}

        <SearchBoxField
          data-help="subheader-search"
          style={{
            ...(!showSearchFilter && { display: "none" }),
          }}
          disabled={disabled}
          value={searchFilterValue}
          appearance="outline"
          placeholder={t("greco.form.searchPlaceholder")}
          onSearch={handleSearch}
          width={isMobile ? "300px" : "360px"}
        />
        {showNeedsAttention && (
          <NeedsAttentionButton count={needsAttentionCount} />
        )}
        {isHrPage && !isNeedsAttentionPage && (
          <Button
            data-help="subheader-ask-question"
            disabled={chatBotDisabled}
            onClick={() => {
              setChatPanelOpen(true);
            }}
            style={{
              gap: "5px",
              color: chatBotDisabled
                ? semanticColors.disabledText
                : palette.purpleDark,
            }}
            icon={
              <ChatIcon
                style={{
                  padding: "3px",
                  background: chatBotDisabled
                    ? semanticColors.disabledText
                    : palette.purpleDark,
                }}
              />
            }
            appearance="subtle"
          >
            {t("hbh.askQuestion.label")}
          </Button>
        )}
      </Wrap>
      {showSearchFilter || isHrPage ? (
        <Flex $align="center" wrap="wrap">
          {showSearchFilter && <ResultsScore score={searchResultsCount} />}
        </Flex>
      ) : null}
    </Container>
  );
}

const Container = styled.div<{ $showSearchFilter?: boolean }>`
  display: flex;
  width: 100%;
  padding: 10px;
  margin: auto;
  position: sticky;
  align-items: center;
  z-index: 99;
  top: 0px;
  height: ${(props) => (props.$showSearchFilter ? "auto" : "52px")};
  background-color: ${(props) => props.theme.palette.white};
  box-sizing: border-box;
  justify-content: space-between;
  gap: 5px;
  border-top: 1px solid ${({ theme }) => theme.palette.neutralLight};

  @media only screen and ${devices.md} {
    padding: 10px 30px;
  }
`;

const Wrap = styled.div`
  display: flex;
  gap: 5px;
  justify-content: flex-start;

  flex-wrap: wrap;
  @media only screen and ${devices.md} {
    justify-content: flex-start;
  }
`;
